<template>
  <span
    v-tooltip:[tooltipArg]="tooltipOptions"
    :data-testid="dataTestid"
    class="standard-tooltip"
  >
    <slot />
  </span>
</template>

<script lang="ts">
/**
 * Thin wrapper around PrimeVue's Tooltip directive (there is no Tooltip component).
 * Applies hover advisory text to the slotted trigger via design-token tooltip styles.
 */
export type StandardTooltipPlacement = Exclude<keyof TooltipDirectiveModifiers, 'focus'>;

export default {
  name: 'StandardTooltip',
  compatConfig: { MODE: 3 },
};
</script>

<script setup lang="ts">
import { computed } from 'vue';
import type { TooltipDirectiveModifiers, TooltipOptions } from 'primevue/tooltip';

/** Class on body-teleported panels; paired with the unscoped rule below. */
const STANDARD_TOOLTIP_OVERLAY_CLASS = 'standard-tooltip-overlay';

const props = withDefaults(
  defineProps<{
    /** Tooltip text. Empty / whitespace disables the tooltip. */
    value: string;
    /**
     * Preferred side of the trigger. Falls back when there is not enough space.
     * @default 'right'
     */
    placement?: StandardTooltipPlacement;
    disabled?: boolean;
    showDelay?: number;
    hideDelay?: number;
    /**
     * When true (default), content is treated as plain text. Set false to allow HTML.
     */
    escape?: boolean;
    dataTestid?: string;
  }>(),
  {
    placement: 'right',
    disabled: false,
    showDelay: 0,
    hideDelay: 0,
    escape: true,
  },
);

/** PrimeVue reads position from the directive argument object, not from TooltipOptions. */
const tooltipArg = computed((): { position: StandardTooltipPlacement } => ({
  position: props.placement,
}));

const tooltipOptions = computed(
  (): TooltipOptions => ({
    value: props.value,
    disabled: props.disabled || props.value.trim() === '',
    showDelay: props.showDelay,
    hideDelay: props.hideDelay,
    escape: props.escape,
    class: STANDARD_TOOLTIP_OVERLAY_CLASS,
  }),
);
</script>

<style lang="scss">
// Unscoped on purpose: the tooltip panel is appended to <body>, so host/scoped CSS cannot
// set its stacking.
//
// Why CSS instead of PrimeVue config / baseZIndex (unlike StandardPopover):
// - Tooltip is a directive, not a component — there is no base-z-index prop.
// - The directive reads zIndex.tooltip from binding.instance.$primevue only (no fallback to
//   app.config.globalProperties). On our @vue/compat app that property is undefined on the
//   host instance, so config is ignored and PV's ZIndex helper falls back to inline ~1000.
// - The nav rail sits at 1036, so ~1000 paints under it.
//
// Do not use v-bind() here: Vue applies those CSS variables on the component host, and the
// teleported panel never sees them.
//
// 1100 matches ABOVE_STICKY_HEADER_Z_INDEX (same floor as menus that clear sticky chrome).
.p-tooltip.standard-tooltip-overlay {
  z-index: 1100 !important;
  max-width: none;

  .p-tooltip-text {
    max-width: none;
  }
}
</style>
